import React, { useEffect, useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@osmosis/react-ui/components/Box';
import { Button } from '@osmosis/react-ui/components/Button';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { faEraser } from '@fortawesome/pro-solid-svg-icons/faEraser';
import Mrkr from 'mrkrjs';
import { useQuizContext } from '../../../../context/QuizContext';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import HighlightTextCursor from '../../../../assets/highlight-text-cursor.png';
import { AnswerHotSpotType, EnhancedHotSpotData } from '../../../../types/quiz';
import { WORD_TOLERANCE } from '../../../../constants/quizeWordTolerance';
import { calculateTolerance, groupingAnswers } from './utils';
import style from './EnhancedHotSpot.module.css';
import { CorrectBanner, SkipQuizButton } from '../utils';

/**
 * EnhancedHotSpotArea.
 */
const EnhancedHotSpotArea = () => {
  const { currentItem, isAnswered, submitAnswer } = useQuizContext();
  const { question, answers } = currentItem?.data as EnhancedHotSpotData;
  const [isSelectedHotspot, setIsSelectedHotspot] = useState<boolean>(false);
  const [marker, setMarker] = useState<Mrkr | null>(null);

  const [isCorrect, setCorrect] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState<AnswerHotSpotType[]>([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState<AnswerHotSpotType[]>(
    [],
  );
  const [missedAnswers, setMissedAnswers] = useState<AnswerHotSpotType[]>([]);

  const highlightData =
    useRef<{ endOffset?: number; startOffset?: number; text: string }[] | null>(null);

  useEffect(() => {
    highlightData.current?.forEach((data) => {
      marker?.highlightRange(data.startOffset ?? 0, data.endOffset ?? 0);
    });
    if (isAnswered) {
      marker?.clear();
      marker?.disableSelection();

      [
        { hotspots: incorrectAnswers, styleName: 'incorrect-highlight' }, // Paint incorrect hotspots
        { hotspots: missedAnswers, styleName: 'missed-highlight' }, // Paint missed hotspots
        { hotspots: correctAnswers, styleName: 'correct-highlight' }, // Paint correct hotspots
      ].forEach(({ hotspots, styleName }) => {
        if (marker && hotspots.length) {
          marker.highlightClass = style[styleName];
          hotspots.forEach((hotspot: AnswerHotSpotType) => {
            marker?.highlightRange(hotspot.startOffset, hotspot.endOffset);
          });
        }
      });
    }
  });

  useEffect(() => {
    if (marker == null) {
      const container =
        document.getElementsByClassName('containerHighlight')[0];
      setMarker(new Mrkr(container as HTMLElement, style['user-highlight']));
    }
  }, [marker]);

  const checkAnswers = () => {
    const answerHotspots = answers.map((hotspot, i) => ({
      ...hotspot,
      tolerance: calculateTolerance(question, hotspot, WORD_TOLERANCE),
      id: i,
    }));

    const userAnswers = highlightData.current;
    const { correctHotspots, incorrectHotspots, missedHotspots } =
      groupingAnswers(userAnswers || [], answerHotspots);

    // Populate any hotspots that were missed
    answerHotspots.forEach((answerHotspot) => {
      if (
        !correctHotspots.find(
          (correctHotspot) => correctHotspot.id === answerHotspot.id,
        ) &&
        !missedHotspots.find(
          (missedHotspot) => missedHotspot.id === answerHotspot.id,
        )
      ) {
        missedHotspots.push(answerHotspot);
      }
    });

    setIncorrectAnswers(incorrectHotspots);
    setCorrectAnswers(correctHotspots);
    setMissedAnswers(missedHotspots);

    const result =
      correctHotspots.length === answerHotspots.length &&
      !incorrectHotspots.length &&
      !missedHotspots.length;

    setCorrect(result);
  };
  // }, [answers, marker, question, highlightData]);

  const checkIsSelected = () => {
    const data = marker?.getData();
    if (data?.length) {
      highlightData.current = data;
      checkAnswers();
      setIsSelectedHotspot(true);
    }
  };

  return (
    <Box width="100%" height="100%" pt={2}>
      <Box
        fontSize={16}
        className={'containerHighlight'}
        sx={{
          cursor: !isAnswered
            ? `url(${HighlightTextCursor.src}) 15 15, pointer`
            : 'auto',
        }}
        onPointerUp={checkIsSelected}
        fontWeight={400}
        textAlign="justify"
        dangerouslySetInnerHTML={{
          __html: question,
        }}
      />

      {!isAnswered && (
        <Button
          variant="transparent"
          color="blue"
          onClick={() => marker?.clear()}
          mb={2}
          startIcon={<Icon icon={faEraser} />}
          data-testid="clear-highlight-btn"
        >
          <FormattedMessage
            id="quiz" // this value is mocked
            defaultMessage="Clear highlighting"
          />
        </Button>
      )}

      {isAnswered && <CorrectBanner isCorrectAnswer={isCorrect} />}
      {isSelectedHotspot && !isAnswered && (
        <QuizSubmitButtons submit={(c) => submitAnswer(c, isCorrect)} />
      )}

      <SkipQuizButton />
    </Box>
  );
};

export default EnhancedHotSpotArea;
